import { useCurrency } from "hooks/user/useCurrencies";
import { DepositOmnibusAccountProps } from "./types";
import { FormattedMessage } from "react-intl";
import { useMemo } from "react";
import { useGetLimits } from "hooks/payments/useGetLimits";
import {
  CurrencyNetwork,
  LimitsReponse,
  OPERATIONS_VALUES,
  REMAINING_LIMITS_CATEGORY,
  TIME_PERIOD_VALUES,
} from "@letsbit/malcolmlb";
import { useFormatter } from "hooks/utils/useFormatter";
import { messages } from "./messages";
import { Pill } from "components/Pill";
import { Limits } from "components/Limits";
import { AlertSign } from "@letsbit/milkylb";
import MessagesComponent from "components/MessagesWithLinks";
import { DepositData } from "../DepositData";
import { DepositFeeWarning } from "components/DepositFeeWarning";

export const DepositOmnibusAccount: React.FC<DepositOmnibusAccountProps> = ({
  depositAddress,
  currencyId,
  paymentNetworkId,
  setPaymentNetwork,
  viewerOnly = false,
}) => {
  const currency = useCurrency(currencyId);

  const { data: operationalLimits } = viewerOnly
    ? { data: undefined }
    : useGetLimits({
        currency: currencyId,
        payment_network: paymentNetworkId || "",
        operation: OPERATIONS_VALUES.DEPOSIT,
        category: REMAINING_LIMITS_CATEGORY.OPERATIONAL,
      });

  const operationalMonthlyLimit = useMemo(() => {
    return operationalLimits?.find(
      (l: LimitsReponse) =>
        l.time_period === TIME_PERIOD_VALUES.MONTHLY && l.kind === "volume",
    );
  }, [operationalLimits]);

  const paymentNetworksSelector = useMemo(
    () =>
      currency?.currency_networks
        .filter((cn) => cn.deposit_enabled)
        .map((n) => {
          return { label: n.payment_network.name, value: n };
        }),
    [currency?.currency_networks],
  );

  const currencyNetworkSelected: CurrencyNetwork | undefined = useMemo(
    () =>
      currency?.currency_networks.filter(
        (cn) =>
          cn.deposit_enabled && cn.payment_network.id === paymentNetworkId,
      )[0],
    [currency?.currency_networks, paymentNetworkId],
  );

  const { formatValue } = useFormatter();

  return (
    <div className="flex w-full flex-col overflow-auto">
      <h3 className="mb-2 text-xl text-[--color-neutral-0]">
        <FormattedMessage {...messages.transferOrigin} />
      </h3>
      <div className="mb-9 flex flex-row items-center gap-x-2">
        {paymentNetworksSelector?.map((paymentNetwork) => (
          <Pill
            onClick={() => {
              setPaymentNetwork(paymentNetwork.value);
            }}
            selected={
              currencyNetworkSelected?.payment_network.id ===
              paymentNetwork.value.payment_network.id
            }
          >
            {paymentNetwork.label}
          </Pill>
        ))}
      </div>
      <div className="flex w-full gap-4">
        <DepositData
          addressData={depositAddress.data}
          paymentNetworkId={paymentNetworkId}
        />
        {currency && (
          <div className="flex w-1/2 flex-col gap-4">
            <Limits
              limitParams={{
                currency: currency.id,
                payment_network: paymentNetworkId,
                operation: OPERATIONS_VALUES.DEPOSIT,
                category: REMAINING_LIMITS_CATEGORY.OPERATIONAL,
              }}
              period={TIME_PERIOD_VALUES.MONTHLY}
            />
            {operationalMonthlyLimit && (
              <AlertSign
                text={
                  <FormattedMessage
                    {...messages.monthlyDeposits}
                    values={{
                      amount: formatValue(
                        operationalMonthlyLimit.value,
                        currency.precision,
                      ),
                      currency: currency.ticker_id.toUpperCase(),
                    }}
                  />
                }
              />
            )}
            <MessagesComponent
              messages={currencyNetworkSelected?.deposit_messages}
            />
            {currencyNetworkSelected && (
              <DepositFeeWarning
                currency={currency}
                currency_network={currencyNetworkSelected}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
